import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { reviewsAPI, employeeAPI } from '../../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Building, Send, Loader2 } from 'lucide-react';

const RequestReview = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [cadence, setCadence] = useState('one-time');
  const [note, setNote] = useState('');

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const res = await employeeAPI.getRecords();
      // Only show acknowledged records
      const acknowledgedRecords = (res.data || []).filter(r => r.status === 'acknowledged');
      setRecords(acknowledgedRecords);
    } catch (error) {
      console.error('Error fetching records:', error);
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedRecordId) {
      toast.error('Please select an employer');
      return;
    }
    
    const selectedRecord = records.find(r => r.id === selectedRecordId);
    if (!selectedRecord) {
      toast.error('Invalid record selected');
      return;
    }
    
    setSubmitting(true);
    try {
      await reviewsAPI.requestReview({
        employer_id: selectedRecord.employer_id,
        record_id: selectedRecordId,
        preferred_cadence: cadence,
        note: note.trim() || null
      });
      
      toast.success('Review request sent!');
      navigate('/employee/reviews');
    } catch (error) {
      console.error('Error requesting review:', error);
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedRecord = records.find(r => r.id === selectedRecordId);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (records.length === 0) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-[#5B6472] hover:text-[#0A0F1E]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
            <Building className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No employers available</h3>
            <p className="text-[#5B6472]">
              You can only request reviews from employers with acknowledged VETT Records.
            </p>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl" data-testid="request-review">
        {/* Header */}
        <div className="animate-fade-in">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-[#5B6472] hover:text-[#0A0F1E] mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">Request a Review</h1>
          <p className="mt-2 text-[#5B6472]">Ask a previous employer to provide a performance review</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Employer Selection */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Select Employer</h2>
            <Select value={selectedRecordId} onValueChange={setSelectedRecordId}>
              <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                <SelectValue placeholder="Choose an employer" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                {records.map(record => (
                  <SelectItem key={record.id} value={record.id} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                    <div className="flex items-center gap-2">
                      <span>{record.employer_name || 'Employer'}</span>
                      <span className="text-[#5B6472]">- {record.job_title}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedRecord && (
              <div className="mt-4 p-4 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                    {selectedRecord.employer_logo ? (
                      <img src={selectedRecord.employer_logo} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <Building className="w-6 h-6 text-[#5B6472]" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-[#0A0F1E]">{selectedRecord.employer_name || 'Employer'}</p>
                    <p className="text-[#5B6472] text-sm">{selectedRecord.job_title}</p>
                    <p className="text-[#5B6472] text-xs">
                      {selectedRecord.start_date} - {selectedRecord.currently_employed ? 'Present' : selectedRecord.end_date}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Review Cadence */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Preferred Review Type</h2>
            <Select value={cadence} onValueChange={setCadence}>
              <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                <SelectItem value="one-time" className="text-[#0A0F1E] hover:bg-[#F5F8FF]">One-Time Review</SelectItem>
                <SelectItem value="weekly" className="text-[#0A0F1E] hover:bg-[#F5F8FF]">Weekly (Recurring)</SelectItem>
                <SelectItem value="monthly" className="text-[#0A0F1E] hover:bg-[#F5F8FF]">Monthly (Recurring)</SelectItem>
                <SelectItem value="quarterly" className="text-[#0A0F1E] hover:bg-[#F5F8FF]">Quarterly (Recurring)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[#5B6472] text-sm mt-2">
              {cadence === 'one-time' 
                ? 'A single performance review.' 
                : `Recurring reviews scheduled automatically.`}
            </p>
          </Card>

          {/* Note */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <div>
              <Label className="text-[#0A0F1E]">Add a Note (Optional)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Let them know why you're requesting a review..."
                className="mt-2 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px]"
                maxLength={500}
              />
              <div className="flex justify-end text-[#5B6472] text-xs mt-1">
                {note.length}/500
              </div>
            </div>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !selectedRecordId}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              data-testid="send-request-btn"
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send Request
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default RequestReview;
